import { useMemo } from "react";
import type { SWRConfiguration } from "swr";
import { firstBy } from "thenby";
import SetComparisonChartService from "services/SetComparisonChartService";
import useQuery from "hooks/useQuery";
import type { MeasurementSet } from "types";

export const getMeasurementSetsCacheKey = (athleteId: string, exerciseId: string) => [
  "SetComparisonChartService.getSets",
  athleteId,
  exerciseId,
];

interface Options extends SWRConfiguration {
  enabled?: boolean;
}

interface GetMeasurementSetsQueryArgs {
  athleteId?: string | null;
  exerciseId?: string | null;
}

const FALLBACK_DATA: MeasurementSet[] = [];

function useGetMeasurementSetsQuery(args: GetMeasurementSetsQueryArgs, options?: Options) {
  const { athleteId, exerciseId } = args;
  const { enabled = true, ...restOptions } = options || {};

  const key = useMemo(() => {
    if (!enabled || !athleteId || !exerciseId) {
      return null;
    }
    return getMeasurementSetsCacheKey(athleteId, exerciseId);
  }, [athleteId, exerciseId, enabled]);

  return useQuery(
    key,
    async ([_, athleteId, exerciseId]) => {
      const sets: MeasurementSet[] = await SetComparisonChartService.getSets(athleteId, { exerciseId });
      return sets.slice().sort(firstBy("completedDate", "desc"));
    },
    {
      ...restOptions,
      fallbackData: FALLBACK_DATA,
    }
  );
}

export default useGetMeasurementSetsQuery;
